import type { TFunction } from "i18next";

import { formatCount } from "@/lib/format";
import { tp } from "@/lib/plural";
import type { WarningItem } from "@/types/api";

/**
 * Traduz o `code` de um aviso estruturado do agente Functional (S-081 Fase
 * 7) para o idioma ativo. Só os 5 avisos do ambiente de demo têm `code`
 * reconhecido aqui — qualquer outro (aviso ainda não migrado, ou de cliente
 * real com aviso novo) cai no `default`, que devolve `item.message` cru,
 * exatamente como o resto do app faz hoje.
 *
 * As chaves usadas em `t()`/`tp()` aqui são **identificadores com ponto**
 * (`warning.<code>.<...>`), não a frase em inglês — diferente do resto do
 * app. É o mesmo estilo de `ref_ecc_scope_mappings.display_name_key`
 * (`reference-data-labels.ts`): a frase de verdade mora só no bundle, em
 * cada idioma; a chave é só uma referência estável. Continuam sendo string
 * **literal** dentro do `switch` (não uma tabela `Record<code, template>`
 * consultada em runtime) para que os guardas de cobertura
 * (`app-i18n-coverage.test.ts`, `i18n-parity.test.ts`), que acham chave por
 * regex estático sobre o código-fonte, continuem enxergando-as.
 */
export function resolveWarningText(t: TFunction, item: WarningItem): string {
  const p = item.params ?? {};
  const count = Number(p.count) || 0;

  switch (item.code) {
    // #944: o ano-base pode ter recuado (params.is_fallback) quando o último
    // ano completo não tinha documento nenhum — são 2 textos possíveis, mesmo
    // code, decisão pelo parâmetro (igual o reason_code do GROW_GAP). Estas
    // chaves são estáveis (não mudam). Se o sentido de um dos textos originais
    // em services/agents/.../nodes/extract_ecc.py (campo "message" do
    // DIGITAL_ACCESS_ESTIMATED) mudar, atualize o VALOR da chave
    // correspondente nos 3
    // frontend/src/i18n/locales/{en-US,pt-BR,es-ES}/translation.json.
    case "DIGITAL_ACCESS_ESTIMATED":
      return p.is_fallback
        ? t("warning.digital_access_estimated.message.fallback", {
            year: p.year,
            lastCompleteYear: p.last_complete_year,
          })
        : t("warning.digital_access_estimated.message", { year: p.year });

    // Estas chaves são estáveis (não mudam). Se o sentido do texto original em
    // services/agents/.../nodes/classify.py (campo "message" do
    // STANDARD_TCODES_UNMAPPED) mudar, atualize o VALOR das duas nos 3
    // frontend/src/i18n/locales/{en-US,pt-BR,es-ES}/translation.json.
    case "STANDARD_TCODES_UNMAPPED": {
      const sample = Array.isArray(p.sample) ? (p.sample as string[]) : [];
      const remaining = Number(p.remaining) || 0;
      const list = remaining > 0 ? `${sample.join(", ")}, …` : sample.join(", ");
      return tp(
        t,
        count,
        "warning.standard_tcodes_unmapped.message.one",
        "warning.standard_tcodes_unmapped.message.other",
        { count: formatCount(count), list },
      );
    }

    // Esta chave é estável (não muda). Se o sentido do texto original em
    // services/agents/.../nodes/map_scope.py (campo "message" do
    // SCOPE_MODULES_UNMAPPED) mudar, atualize o VALOR desta chave nos 3
    // frontend/src/i18n/locales/{en-US,pt-BR,es-ES}/translation.json.
    case "SCOPE_MODULES_UNMAPPED": {
      const modules = Array.isArray(p.modules) ? (p.modules as string[]) : [];
      return t("warning.scope_modules_unmapped.message", {
        modules: modules.join(", "),
      });
    }

    // Estas chaves são estáveis (não mudam). Se o sentido do texto original em
    // services/agents/.../scope_activation/grow_catalog.py (campo "message"
    // do GROW_GAP) mudar, atualize o VALOR das duas nos 3
    // frontend/src/i18n/locales/{en-US,pt-BR,es-ES}/translation.json.
    case "GROW_GAP": {
      const sample = Array.isArray(p.sample) ? (p.sample as string[]) : [];
      const remaining = Number(p.remaining) || 0;
      const reason = growGapReasonText(
        t,
        p.reason_code as string | undefined,
        p.reason_params as Record<string, unknown> | undefined,
        count,
      );
      const list =
        sample.join(", ") +
        (remaining > 0
          ? t("warning.grow_gap.remaining_suffix", { remaining: formatCount(remaining) })
          : "");
      return tp(
        t,
        count,
        "warning.grow_gap.message.one",
        "warning.grow_gap.message.other",
        { count: formatCount(count), reason, country: p.country, release: p.release, list },
      );
    }

    // Estas chaves são estáveis (não mudam). Se o sentido do texto original em
    // services/agents/.../scope_activation/migration_disposition.py (campo
    // "message" do MIGRATION_SIC_TRIGGERED) mudar, atualize o VALOR das duas
    // nos 3 frontend/src/i18n/locales/{en-US,pt-BR,es-ES}/translation.json.
    case "MIGRATION_SIC_TRIGGERED":
      return tp(
        t,
        count,
        "warning.migration_sic_triggered.message.one",
        "warning.migration_sic_triggered.message.other",
        { count: formatCount(count) },
      );

    // Estas chaves são estáveis (não mudam). Se o sentido do texto original em
    // services/agents/.../nodes/classify.py (campo "message" do
    // CUSTOM_TCODES_UNMAPPED) mudar, atualize o VALOR das duas nos 3
    // frontend/src/i18n/locales/{en-US,pt-BR,es-ES}/translation.json.
    case "CUSTOM_TCODES_UNMAPPED": {
      const sample = Array.isArray(p.sample) ? (p.sample as string[]) : [];
      const remaining = Number(p.remaining) || 0;
      const list = remaining > 0 ? `${sample.join(", ")}, …` : sample.join(", ");
      return tp(
        t,
        count,
        "warning.custom_tcodes_unmapped.message.one",
        "warning.custom_tcodes_unmapped.message.other",
        { count: formatCount(count), list },
      );
    }

    // Original em services/agents/.../nodes/resolve_skus.py (campo "message"
    // do PREMIUM_DOWNGRADED_TO_BASE).
    case "PREMIUM_DOWNGRADED_TO_BASE":
      return tp(
        t,
        count,
        "warning.premium_downgraded_to_base.message.one",
        "warning.premium_downgraded_to_base.message.other",
        { count: formatCount(count) },
      );

    // Original em services/agents/.../nodes/resolve_skus.py (campo "message"
    // do TECHNICAL_ACCESS_NOT_DEVELOPER).
    case "TECHNICAL_ACCESS_NOT_DEVELOPER": {
      const tcodes = Array.isArray(p.tcodes) ? (p.tcodes as string[]) : [];
      return tp(
        t,
        count,
        "warning.technical_access_not_developer.message.one",
        "warning.technical_access_not_developer.message.other",
        {
          count: formatCount(count),
          tcodes: tcodes.join(", "),
          authored: formatCount(Number(p.authored) || 0),
        },
      );
    }

    // Original em services/agents/.../functional_v2/helpers.py
    // (`unpriced_warning`, campo "message" do BOM_LINES_UNPRICED).
    case "BOM_LINES_UNPRICED": {
      const skus = Array.isArray(p.skus) ? (p.skus as string[]) : [];
      const onRequest = Array.isArray(p.on_request) ? (p.on_request as string[]) : [];
      let text = tp(
        t,
        count,
        "warning.bom_lines_unpriced.message.one",
        "warning.bom_lines_unpriced.message.other",
        { count: formatCount(count), skus: skus.join(", ") },
      );
      if (onRequest.length > 0) {
        text += t("warning.bom_lines_unpriced.on_request", {
          count: formatCount(onRequest.length),
        });
        // "o resto" só existe quando há resto: com todas as linhas sob
        // consulta, a frase mandaria semear uma referência sem buraco.
        if (onRequest.length < count) {
          text += t("warning.bom_lines_unpriced.rest_missing");
        }
      }
      return text;
    }

    // Original em services/sap-connector/.../s4hana/digital_access.py (campo
    // "reason", com o "reason_code" ao lado).
    case "DIGITAL_ACCESS_NOT_COMPUTABLE":
      return p.reason_code === "DAC_TECHUSER_EMPTY"
        ? t("warning.digital_access_not_computable.dac_techuser_empty")
        : item.message;

    // Original em services/agents/.../nodes/extract_ecc.py (campo "message"
    // do ROLE_TCODE_TRUNCATED).
    case "ROLE_TCODE_TRUNCATED":
      return t("warning.role_tcode_truncated.message");

    // Original em services/agents/.../nodes/extract_ecc.py (campo "message"
    // do USER_LANDSCAPE_TRUNCATED).
    case "USER_LANDSCAPE_TRUNCATED": {
      const entities = Array.isArray(p.entities) ? (p.entities as string[]) : [];
      return t("warning.user_landscape_truncated.message", {
        entities: entities.join(", "),
      });
    }

    // Original em services/agents/.../nodes/extract_ecc.py (campo "message"
    // do ROLE_TCODE_WILDCARD).
    case "ROLE_TCODE_WILDCARD": {
      const sample = Array.isArray(p.sample) ? (p.sample as string[]) : [];
      const remaining = Number(p.remaining) || 0;
      const list = remaining > 0 ? `${sample.join(", ")}, …` : sample.join(", ");
      return tp(
        t,
        count,
        "warning.role_tcode_wildcard.message.one",
        "warning.role_tcode_wildcard.message.other",
        { count: formatCount(count), list },
      );
    }

    // Original em services/agents/.../nodes/map_scope.py (campo "message"
    // do SEMANTIC_SEARCH_UNAVAILABLE).
    case "SEMANTIC_SEARCH_UNAVAILABLE": {
      const modules = Array.isArray(p.modules) ? (p.modules as string[]) : [];
      return tp(
        t,
        count,
        "warning.semantic_search_unavailable.message.one",
        "warning.semantic_search_unavailable.message.other",
        { count: formatCount(count), modules: modules.join(", ") },
      );
    }

    // Original em services/agents/.../nodes/resolve_skus.py (campo "message"
    // do UNMODELED_SCM_PREMIUM). Era um aviso por usuário, e virava seis
    // linhas quase idênticas na tela; agora é um só, agregado.
    case "UNMODELED_SCM_PREMIUM": {
      const tcodes = Array.isArray(p.tcodes) ? (p.tcodes as string[]) : [];
      return tp(
        t,
        count,
        "warning.unmodeled_scm_premium.message.one",
        "warning.unmodeled_scm_premium.message.other",
        { count: formatCount(count), tcodes: tcodes.join(", ") },
      );
    }

    default:
      return item.message;
  }
}

// As 3 chaves abaixo são estáveis (não mudam). Se o sentido de um motivo em
// services/agents/.../scope_activation/grow_catalog.py, dentro de
// _grow_decision() (um `return` por reason_code), mudar, atualize o VALOR da
// chave correspondente nos 3
// frontend/src/i18n/locales/{en-US,pt-BR,es-ES}/translation.json.
function growGapReasonText(
  t: TFunction,
  reasonCode: string | undefined,
  reasonParams: Record<string, unknown> | undefined,
  count: number,
): string {
  // O motivo entra no meio da frase do aviso, que já é singular ou plural pelo
  // `count`. Ele também precisa concordar: sem isso a tela dizia "53 scope
  // items marcado indisponível" em pt-BR e "53 scope items marcado como no
  // disponible" em es-ES. Em inglês não aparecia, porque particípio não
  // flexiona, e foi por isso que passou despercebido (#1113).
  switch (reasonCode) {
    case "NOT_IN_CATALOG":
      return tp(
        t,
        count,
        "warning.grow_gap.reason.not_in_catalog.one",
        "warning.grow_gap.reason.not_in_catalog.other",
      );
    case "UNAVAILABLE":
      return tp(
        t,
        count,
        "warning.grow_gap.reason.unavailable.one",
        "warning.grow_gap.reason.unavailable.other",
      );
    case "RELEASE_TOO_NEW":
      return tp(
        t,
        count,
        "warning.grow_gap.reason.release_too_new.one",
        "warning.grow_gap.reason.release_too_new.other",
        { fromRelease: reasonParams?.from_release },
      );
    default:
      return "";
  }
}
